import type { CostEntry } from '@orgus/protocol';
import { formatUsd } from '@orgus/protocol';
import { FileUp } from '@orgus/ui/icons';
import { useEffect, useRef, useState } from 'react';
import { Button, Dialog, ErrorState, plural, toUiError, toast, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { useProjects } from '../../lib/store.ts';
import { previewInvoiceCsv } from './csv.ts';
import { importInvoices } from './useFinance.ts';

const EXAMPLE = 'date,to,vendor,description,amount,category,project,invoice\n2026-10-01,2026-10-31,Cloudflare,Workers Paid,25.00,platform,orgus,CF-2210';

/** Import platform invoices from a CSV: preview first, then all rows or none. */
export function ImportDialog({ open, onOpenChange }: { open: boolean; onOpenChange(v: boolean): void }) {
  const projects = useProjects();
  const [text, setText] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setText(null);
      setName('');
      setError(null);
    }
  }, [open]);

  const preview = text === null ? null : previewInvoiceCsv(text, projects);

  const pick = async (f: File | undefined) => {
    setError(null);
    if (!f) return;
    if (f.size > 900_000) {
      setError({ what: 'That file is too large.', why: 'Import at most about 900 KB of CSV at a time.', next: 'Split the file and import each part.' });
      return;
    }
    setName(f.name);
    setText(await f.text());
  };

  const submit = async () => {
    if (!text || !preview || !preview.ok) return;
    setBusy(true);
    setError(null);
    try {
      const added = await importInvoices(text, preview.entries);
      toast(`Imported ${plural(added, 'invoice row')}`, { tone: 'success' });
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e, { what: 'Nothing was imported.' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      dismissible={!busy}
      size="lg"
      title="Import invoices"
      description="Rows without a category go to Platform."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} disabled={!preview?.ok || !preview.entries.length} onClick={() => void submit()}>
            {preview?.ok && preview.entries.length ? `Import ${plural(preview.entries.length, 'row')}` : 'Import'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="sr-only" aria-label="Invoice CSV file" onChange={(e) => void pick(e.target.files?.[0])} />
          <Button size="sm" variant="secondary" icon={<FileUp />} onClick={() => fileRef.current?.click()}>
            {text === null ? 'Choose a CSV file' : 'Choose another file'}
          </Button>
          <span className="min-w-0 truncate text-sm text-fg-muted">{name || 'Nothing chosen yet'}</span>
        </div>
        {text === null && <pre className="overflow-x-auto rounded-md border border-border-subtle bg-bg px-3 py-2 font-mono text-xs text-fg-muted">{EXAMPLE}</pre>}
        {preview && !preview.ok && (
          <ErrorState error={{ what: 'This file cannot be imported yet.', why: preview.why, next: 'Fix the file and choose it again.' }} variant="inline" />
        )}
        {preview?.ok && (
          <div className="overflow-hidden rounded-md border border-border">
            <Table aria-label="Rows to import">
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead numeric>Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.entries.slice(0, 8).map((e: CostEntry) => (
                  <TableRow key={e.id}>
                    <TableCell primary>{e.vendor}</TableCell>
                    <TableCell className="num">{e.from === e.to ? e.from : `${e.from} to ${e.to}`}</TableCell>
                    <TableCell>{e.projectId ? (projects.find((p) => p.id === e.projectId)?.name ?? e.projectId) : 'Shared'}</TableCell>
                    <TableCell numeric className="text-fg">
                      {formatUsd(e.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="border-t border-border-subtle px-4 py-2 text-caption text-fg-subtle">
              {plural(preview.entries.length, 'row')}, {formatUsd(preview.entries.reduce((a, e) => a + e.amount, 0))} in total
              {preview.entries.length > 8 ? `; the first 8 are shown` : ''}.
            </p>
          </div>
        )}
        {error && <ErrorState error={error} variant="inline" />}
      </div>
    </Dialog>
  );
}
